import React, { useState, useEffect } from "react";
import { Button, Spin, Radio, Table, Card } from "antd";
import {
  EnvironmentOutlined,
  ExportOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import indiaGeoJson from "./indiaStates.json";
import { reducer, sliceKey, actions } from "./slice";
import { sampleCoverageSaga } from "./saga";
import * as selectors from "./selectors";

const getColor = (rate) => {
  if (rate === undefined || rate === null || rate === "No data")
    return "#ffffff"; // White for no data

  // Ensure rate is a number and clamp between 0 and 100
  const numRate = Math.max(0, Math.min(100, Number(rate)));

  // Create gradient color mapping
  if (numRate >= 90) {
    // Green gradient (90-100): darker to lighter green
    const intensity = (numRate - 90) / 10; // 0 to 1
    const r = Math.round(46 - (46 - 39) * intensity); // 46 to 39
    const g = Math.round(204 - (204 - 174) * intensity); // 204 to 174
    const b = Math.round(113 - (113 - 96) * intensity); // 113 to 96
    return `rgb(${r}, ${g}, ${b})`;
  } else if (numRate >= 80) {
    // Yellow-Green gradient (80-90): yellow to green
    const intensity = (numRate - 80) / 10; // 0 to 1
    const r = Math.round(241 - (241 - 46) * intensity); // 241 to 46
    const g = Math.round(196 - (196 - 204) * intensity); // 196 to 204
    const b = Math.round(15 - (15 - 113) * intensity); // 15 to 113
    return `rgb(${r}, ${g}, ${b})`;
  } else if (numRate >= 70) {
    // Orange-Yellow gradient (70-80): orange to yellow
    const intensity = (numRate - 70) / 10; // 0 to 1
    const r = Math.round(230 - (230 - 241) * intensity); // 230 to 241
    const g = Math.round(126 - (126 - 196) * intensity); // 126 to 196
    const b = Math.round(34 - (34 - 15) * intensity); // 34 to 15
    return `rgb(${r}, ${g}, ${b})`;
  } else if (numRate >= 50) {
    // Red-Orange gradient (50-70): red to orange
    const intensity = (numRate - 50) / 20; // 0 to 1
    const r = Math.round(231 - (231 - 230) * intensity); // 231 to 230
    const g = Math.round(76 - (76 - 126) * intensity); // 76 to 126
    const b = Math.round(60 - (60 - 34) * intensity); // 60 to 34
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Dark red gradient (0-50): very dark red to red
    const intensity = numRate / 50; // 0 to 1
    const r = Math.round(139 + (231 - 139) * intensity); // 139 to 231
    const g = Math.round(26 + (76 - 26) * intensity); // 26 to 76
    const b = Math.round(26 + (60 - 26) * intensity); // 26 to 60
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const IndiaMap = ({
  filters = {},
  onDetailedClick = () => {},
  mapType = "sampling", // "sampling" or "compliance"
}) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: sampleCoverageSaga });

  const dispatch = useDispatch();
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });
  const [viewMode, setViewMode] = useState(
    mapType === "sampling" ? "completion" : "compliance"
  ); // "completion", "sampling", or "compliance"
  const [complianceDimension, setComplianceDimension] = useState("Overall"); // Internal compliance dimension state
  const [selectedState, setSelectedState] = useState(null);
  const [showDistrictTable, setShowDistrictTable] = useState(false);

  const loading = useSelector(selectors.selectLoading);
  const data = useSelector(selectors.selectSampleCoverageData);
  const error = useSelector(selectors.selectError);
  const districtLoading = useSelector(selectors.selectDistrictLoading);
  const allDistrictData = useSelector(selectors.selectDistrictData);
  const districtError = useSelector(selectors.selectDistrictError);

  const complianceLoading = useSelector(selectors.selectComplianceLoading);
  const complianceData = useSelector(selectors.selectComplianceData);
  const complianceError = useSelector(selectors.selectComplianceError);

  const filtersString = JSON.stringify(filters);

  useEffect(() => {
    if (mapType === "sampling") {
      dispatch(actions.fetchSampleCoverage({ filtersString: filtersString }));
      dispatch(actions.fetchDistrictData({ filtersString: filtersString }));
    } else if (mapType === "compliance") {
      dispatch(
        actions.fetchComplianceData({
          filtersString: filtersString,
          dimension: complianceDimension,
        })
      );
    }
  }, [filtersString, dispatch, mapType, complianceDimension]);

  // Transform API data to state-wise lookup
  const stateData = React.useMemo(() => {
    if (mapType === "sampling") {
      if (!data || !Array.isArray(data)) return {};
      return data.reduce((acc, item) => {
        const samplingScore =
          item.totalOrders > 0
            ? Math.round((item.sampleCollected / item.totalOrders) * 100)
            : 0;
        acc[item.state] = {
          completionRate: item.completionRate,
          totalOrders: item.totalOrders,
          sampleCollected: item.sampleCollected,
          completedOrders: item.completedOrders,
          samplingScore: samplingScore,
        };
        return acc;
      }, {});
    } else if (mapType === "compliance") {
      if (
        !complianceData?.data?.states ||
        !Array.isArray(complianceData.data.states)
      )
        return {};

      return complianceData.data.states.reduce((acc, item) => {
        let compliantCases = item.compliantCases || 0;
        let nonCompliantCases = item.nonCompliantCases || 0;
        let totalChecks = item.totalChecks || 0;

        // ✅ Apply dimension logic here
        if (complianceDimension === "Safety") {
          compliantCases = item.complianceDetails?.safeCount || 0;
          nonCompliantCases = item.complianceDetails?.unsafeCount || 0;
          totalChecks = item.complianceDetails?.totalSafety || 0;
        }
        if (complianceDimension === "Quality") {
          compliantCases = item.complianceDetails?.standardCount || 0;
          nonCompliantCases = item.complianceDetails?.subStandardCount || 0;
          totalChecks = item.complianceDetails?.totalQuality || 0;
        }
        if (complianceDimension === "Labelling") {
          compliantCases = item.complianceDetails?.brandedCount || 0;
          nonCompliantCases = item.complianceDetails?.mislabelledCount || 0;
          totalChecks = item.complianceDetails?.totalLabeling || 0;
        }

        acc[item.state] = {
          complianceRate: item.stateComplianceRate,
          totalOrders: item.totalOrders,
          totalChecks,
          compliantCases,
          nonCompliantCases,
        };
        return acc;
      }, {});
    }
    return {};
  }, [data, complianceData, mapType, complianceDimension]);
  // Filter district data for selected state
  const districtTableData = React.useMemo(() => {
    if (!selectedState) return [];

    if (mapType === "sampling") {
      if (!allDistrictData || !Array.isArray(allDistrictData)) return [];
      return allDistrictData
        .filter((item) => item.state === selectedState)
        .map((item, index) => ({
          key: index,
          district: item.district,
          completionRate: item.completionRate || "0.00",
          sampleCollectionScore: item.sampleCollectionScore || "0.00",
          totalOrders: item.totalOrders || 0,
          sampleCollected: item.sampleCollected || 0,
          completedOrders: item.completedOrders || 0,
        }));
    } else if (mapType === "compliance") {
      if (!complianceData?.data?.states) return [];
      const stateInfo = complianceData.data.states.find(
        (state) => state.state === selectedState
      );
      if (!stateInfo?.districts) return [];
      return stateInfo.districts.map((item, index) => {
        let relevantItem = {
          key: index,
          district: item.district,
          complianceRate: item.districtComplianceRate || "0.00",
          totalOrders: item.totalOrders || 0,
          totalChecks: item.totalChecks || 0,
          compliantCases: item.compliantCases || 0,
          nonCompliantCases: item.nonCompliantCases || 0,
        };
        if (complianceDimension === "Safety") {
          relevantItem.nonCompliantCases = item.complianceDetails?.unsafeCount;
          relevantItem.compliantCases = item.complianceDetails?.safeCount;
          relevantItem.totalChecks = item.complianceDetails?.totalSafety;
        }
        if (complianceDimension === "Quality") {
          relevantItem.nonCompliantCases =
            item.complianceDetails?.subStandardCount;
          relevantItem.compliantCases = item.complianceDetails?.standardCount;
          relevantItem.totalChecks = item.complianceDetails?.totalQuality;
        }
        if (complianceDimension === "Labelling") {
          relevantItem.nonCompliantCases =
            item.complianceDetails?.mislabelledCount;
          relevantItem.compliantCases = item.complianceDetails?.brandedCount;
          relevantItem.totalChecks = item.complianceDetails?.totalLabeling;
        }
        return relevantItem;
      });
    }
    return [];
  }, [
    allDistrictData,
    complianceData,
    selectedState,
    mapType,
    complianceDimension,
  ]);

  const handleMouseEnter = (event, geo) => {
    const stateName = geo.properties.st_nm;
    const stateInfo = stateData[stateName];

    if (mapType === "sampling") {
      if (viewMode === "completion") {
        const rate = stateInfo?.completionRate ?? "No data";
        const totalOrders = stateInfo?.totalOrders ?? "N/A";
        const sampleCollected = stateInfo?.sampleCollected ?? "N/A";

        setTooltip({
          visible: true,
          x: event.clientX + 10,
          y: event.clientY - 40,
          content: (
            <div>
              <strong>{stateName}</strong>
              <br />
              {typeof rate === "number"
                ? `Completion Rate: ${rate}%`
                : "Completion Rate: N/A"}
              <br />
              Total Orders: {totalOrders}
              <br />
              Sample Collected: {sampleCollected}
              <div
                style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}
              >
                {selectedState === stateName
                  ? "Click to deselect"
                  : "Click to view districts"}
              </div>
            </div>
          ),
        });
      } else {
        const samplingScore = stateInfo?.samplingScore ?? "No data";
        const totalOrders = stateInfo?.totalOrders ?? "N/A";
        const sampleCollected = stateInfo?.sampleCollected ?? "N/A";

        setTooltip({
          visible: true,
          x: event.clientX + 10,
          y: event.clientY - 40,
          content: (
            <div>
              <strong>{stateName}</strong>
              <br />
              {typeof samplingScore === "number"
                ? `Sampling Score: ${samplingScore}%`
                : "Sampling Score: N/A"}
              <br />
              Total Orders: {totalOrders}
              <br />
              Sample Collected: {sampleCollected}
              <div
                style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}
              >
                {selectedState === stateName
                  ? "Click to deselect"
                  : "Click to view districts"}
              </div>
            </div>
          ),
        });
      }
    } else if (mapType === "compliance") {
      const complianceRate = stateInfo?.complianceRate ?? "No data";
      const totalOrders = stateInfo?.totalOrders ?? "N/A";
      const totalChecks = stateInfo?.totalChecks ?? "N/A";
      const compliantCases = stateInfo?.compliantCases ?? "N/A";

      setTooltip({
        visible: true,
        x: event.clientX + 10,
        y: event.clientY - 40,
        content: (
          <div>
            <strong>{stateName}</strong>
            <br />
            {typeof complianceRate === "number"
              ? `Compliance Rate: ${complianceRate}%`
              : "Compliance Rate: N/A"}
            <br />
            Total Orders: {totalOrders}
            <br />
            Total Checks: {totalChecks}
            <br />
            Compliant Cases: {compliantCases}
            <div style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}>
              {selectedState === stateName
                ? "Click to deselect"
                : "Click to view districts"}
            </div>
          </div>
        ),
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, content: "" });
  };

  const handleStateClick = (geo) => {
    const stateName = geo.properties.st_nm;

    if (selectedState === stateName) {
      setSelectedState(null);
      setShowDistrictTable(false);
    } else {
      setSelectedState(stateName);
      setShowDistrictTable(true);
    }
  };

  const handleCloseTable = () => {
    setShowDistrictTable(false);
    setSelectedState(null);
  };

  // const handleDetailedClickWithDimension = () => {
  //     if (mapType === "compliance") {
  //         onDetailedClick(complianceDimension);
  //     } else {
  //         onDetailedClick();
  //     }
  // };

  const getStateColor = (stateName) => {
    const stateInfo = stateData[stateName];
    if (!stateInfo) return getColor(undefined);

    let value;
    if (mapType === "sampling") {
      value =
        viewMode === "completion"
          ? stateInfo.completionRate
          : stateInfo.samplingScore;
    } else if (mapType === "compliance") {
      value = stateInfo.complianceRate;
    }

    return getColor(value);
  };

  // Table columns
  const getTableColumns = () => {
    const baseColumns = [
      {
        title: "District",
        dataIndex: "district",
        key: "district",
        width: "25%",
        sorter: (a, b) => a.district.localeCompare(b.district),
        defaultSortOrder: "ascend",
      },
    ];

    if (mapType === "sampling") {
      baseColumns.push(
        {
          title: "Total Orders",
          dataIndex: "totalOrders",
          key: "totalOrders",
          width: "20%",
          align: "center",
        },
        {
          title: "Sample Collected",
          dataIndex: "sampleCollected",
          key: "sampleCollected",
          width: "25%",
          align: "center",
        }
      );

      if (viewMode === "completion") {
        baseColumns.push({
          title: "Completion Rate (%)",
          dataIndex: "completionRate",
          key: "completionRate",
          width: "24%",
          sorter: (a, b) =>
            parseFloat(a.completionRate) - parseFloat(b.completionRate),
          align: "center",
          render: (value) => {
            const num = parseFloat(value);

            if (!isNaN(num)) {
              return (
                <span
                  style={{
                    color:
                      num >= 80 ? "#52c41a" : num >= 50 ? "#faad14" : "#f5222d",
                    fontWeight: "bold",
                  }}
                >
                  {num}%
                </span>
              );
            }

            return <span style={{ fontWeight: "bold" }}>N/A</span>;
          },
        });
      } else {
        baseColumns.push({
          title: "Sample Collection Score (%)",
          dataIndex: "sampleCollectionScore",
          key: "sampleCollectionScore",
          width: "24%",
          sorter: (a, b) =>
            parseFloat(a.sampleCollectionScore) -
            parseFloat(b.sampleCollectionScore),
          align: "center",
          render: (value) => {
            const num = parseFloat(value);

            if (!isNaN(num)) {
              return (
                <span
                  style={{
                    color:
                      num >= 80 ? "#52c41a" : num >= 50 ? "#faad14" : "#f5222d",
                    fontWeight: "bold",
                  }}
                >
                  {num}%
                </span>
              );
            }

            return <span style={{ fontWeight: "bold" }}>N/A</span>;
          },
        });
      }
    } else if (mapType === "compliance") {
      // Determine dynamic labels based on complianceDimension
      const nonCompliantLabel =
        complianceDimension === "Quality"
          ? "Sub-Standard Samples"
          : complianceDimension === "Safety"
          ? "Unsafe Samples"
          : "Non-Compliant Cases";

      const compliantLabel =
        complianceDimension === "Quality"
          ? "Standard Samples"
          : complianceDimension === "Safety"
          ? "Safe Samples"
          : "Compliant Cases";

      baseColumns.push(
        {
          title: "Total Orders",
          dataIndex: "totalOrders",
          key: "totalOrders",
          width: "20%",
          align: "center",
        },
        {
          title: "Total Checks",
          dataIndex: "totalChecks",
          key: "totalChecks",
          width: "20%",
          align: "center",
        },
        {
          title: nonCompliantLabel,
          dataIndex: "nonCompliantCases",
          key: "nonCompliantCases",
          width: "26%",
          align: "center",
        },
        {
          title: compliantLabel,
          dataIndex: "compliantCases",
          key: "compliantCases",
          width: "26%",
          align: "center",
        },
        {
          title: "Compliance Rate (%)",
          dataIndex: "complianceRate",
          key: "complianceRate",
          width: "20%",
          sorter: (a, b) =>
            parseFloat(a.complianceRate) - parseFloat(b.complianceRate),
          align: "center",
          render: (value) => {
            const num = parseFloat(value);

            if (!isNaN(num)) {
              return (
                <span
                  style={{
                    color:
                      num >= 80 ? "#52c41a" : num >= 50 ? "#faad14" : "#f5222d",
                    fontWeight: "bold",
                  }}
                >
                  {num}%
                </span>
              );
            }

            return <span style={{ fontWeight: "bold" }}>N/A</span>;
          },
        }
      );
    }

    return baseColumns;
  };

  const getHeaderTitle = () => {
    if (mapType === "sampling") {
      return "Sampling Coverage - Geography";
    } else if (mapType === "compliance") {
      return `Compliance Heat-Maps`;
    }
    return "Map View";
  };

  const getLegendTitle = () => {
    if (mapType === "sampling") {
      return viewMode === "completion"
        ? "Completion Score"
        : "Sampling Completion";
    } else if (mapType === "compliance") {
      return "Compliance Rate";
    }
    return "Score";
  };

  const currentLoading = mapType === "sampling" ? loading : complianceLoading;
  const currentError = mapType === "sampling" ? error : complianceError;

  return (
    <div style={{ background: "#f5f5f5" }}>
      {/* Custom Header */}
      <div
        style={{
          background: "#1e2a38",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTopLeftRadius: "0px",
          borderTopRightRadius: "0px",
        }}
      >
        <h4
          style={{
            color: "#fff",
            margin: 0,
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          {getHeaderTitle()}
        </h4>
        {mapType === "sampling" && (
          <Button
            onClick={onDetailedClick}
            icon={<ExportOutlined style={{ color: "#fff" }} />}
            style={{
              backgroundColor: "#1e2a38",
              border: "1px solid #fff",
              color: "#fff",
            }}
          >
            Detailed View
          </Button>
        )}
      </div>

      {/* Filter Section */}
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #e8e8e8",
          background: "#ffffff",
        }}
      >
        {mapType === "sampling" && (
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={{ fontWeight: "500" }}
          >
            <Radio.Button value="completion">Completion Score</Radio.Button>
            <Radio.Button value="sampling">Sampling Score</Radio.Button>
          </Radio.Group>
        )}

        {mapType === "compliance" && (
          <Radio.Group
            value={complianceDimension}
            onChange={(e) => setComplianceDimension(e.target.value)}
            style={{ fontWeight: "500" }}
          >
            <Radio.Button value="Overall">Overall</Radio.Button>
            <Radio.Button value="Safety">Safety</Radio.Button>
            <Radio.Button value="Quality">Quality</Radio.Button>
            <Radio.Button value="Labelling">Labelling</Radio.Button>
          </Radio.Group>
        )}
      </div>

      {/* Body Content */}
      <div style={{ padding: 24 }}>
        {currentLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : currentError ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#ff4d4f" }}
          >
            Error loading data: {currentError.message || "Something went wrong"}
          </div>
        ) : (
          <div
            style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}
          >
            {/* Map Container */}
            <div
              style={{
                position: "relative",
                width: showDistrictTable ? "60%" : "100%",
                maxWidth: showDistrictTable ? "600px" : "900px",
                transition: "width 0.3s ease",
              }}
            >
              {/* Legend - Always positioned relative to map */}
              <div
                style={{
                  position: "absolute",
                  bottom: -65,
                  left: 20,
                  background: "white",
                  padding: showDistrictTable ? "8px 12px" : "10px 15px",
                  borderRadius: 5,
                  fontSize: showDistrictTable ? 10 : 12,
                  boxShadow: "0 0 5px rgba(0,0,0,0.3)",
                  maxWidth: showDistrictTable ? "140px" : "180px",
                  width: showDistrictTable ? "140px" : "auto",
                  zIndex: 10,
                  transition: "all 0.3s ease",
                }}
              >
                <strong
                  style={{ fontSize: showDistrictTable ? "10px" : "12px" }}
                >
                  {getLegendTitle()}
                </strong>
                <br />

                {/* Gradient Bar */}
                <div
                  style={{
                    width: showDistrictTable ? "110px" : "150px",
                    height: showDistrictTable ? "14px" : "20px",
                    background:
                      "linear-gradient(to right, rgb(139,26,26) 0%, rgb(231,76,60) 25%, rgb(230,126,34) 50%, rgb(241,196,15) 75%, rgb(46,204,113) 100%)",
                    border: "1px solid #ccc",
                    marginTop: showDistrictTable ? "4px" : "6px",
                    marginBottom: showDistrictTable ? "4px" : "6px",
                    transition: "all 0.3s ease",
                  }}
                ></div>

                {/* Scale labels */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: showDistrictTable ? "110px" : "150px",
                    fontSize: showDistrictTable ? "8px" : "10px",
                    color: "#666",
                    transition: "all 0.3s ease",
                  }}
                >
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>

                <div
                  style={{
                    marginTop: showDistrictTable ? "4px" : "6px",
                    fontSize: showDistrictTable ? "8px" : "10px",
                    color: "#888",
                    lineHeight: showDistrictTable ? "1.2" : "1.4",
                    transition: "all 0.3s ease",
                  }}
                >
                  <>
                    <div>90%+ (Excellent) | 80-89% (Good)</div>
                    <div>70-79% (Average) | 50-69% (Fair) | &lt;50% (Poor)</div>
                  </>
                  <div
                    style={{
                      marginTop: showDistrictTable ? "2px" : "4px",
                      fontStyle: "italic",
                      color: "#1890ff",
                    }}
                  >
                    {showDistrictTable
                      ? "Click to deselect"
                      : `Click states to ${
                          selectedState ? "deselect or " : ""
                        }view districts`}
                  </div>
                </div>
              </div>
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                  center: [82.8, 22.5],
                  scale: 900,
                }}
                style={{ width: "100%", height: "auto" }}
              >
                <Geographies geography={indiaGeoJson}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getStateColor(geo.properties.st_nm)}
                        stroke="#666"
                        strokeWidth={
                          selectedState === geo.properties.st_nm ? 2 : 0.3
                        }
                        onMouseEnter={(e) => handleMouseEnter(e, geo)}
                        onMouseMove={(e) =>
                          setTooltip((t) => ({
                            ...t,
                            x: e.clientX + 10,
                            y: e.clientY - 40,
                          }))
                        }
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleStateClick(geo)}
                        style={{
                          default: { outline: "none", cursor: "pointer" },
                          hover: {
                            fill:
                              selectedState === geo.properties.st_nm
                                ? getStateColor(geo.properties.st_nm)
                                : "#999",
                            outline: "none",
                            cursor: "pointer",
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>
              </ComposableMap>

              {/* Floating Tooltip */}
              {tooltip.visible && (
                <div
                  style={{
                    position: "fixed",
                    top: tooltip.y,
                    left: tooltip.x,
                    background: "white",
                    padding: "8px 12px",
                    borderRadius: 5,
                    boxShadow: "0 0 8px rgba(0,0,0,0.3)",
                    fontSize: 12,
                    pointerEvents: "none",
                    zIndex: 1000,
                    whiteSpace: "nowrap",
                  }}
                >
                  {tooltip.content}
                </div>
              )}
            </div>

            {/* District Table */}
            {showDistrictTable && (
              <div style={{ width: "40%", minWidth: "450px" }}>
                <Card
                  title={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                        {selectedState} - Districts
                      </span>
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={handleCloseTable}
                        size="small"
                        style={{ color: "#666" }}
                      />
                    </div>
                  }
                  style={{ height: "fit-content", maxHeight: "650px" }}
                  bodyStyle={{ padding: 0 }}
                >
                  {districtLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <Spin size="large" />
                      <div style={{ marginTop: "16px" }}>
                        Loading district data...
                      </div>
                    </div>
                  ) : districtError ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#ff4d4f",
                      }}
                    >
                      Error loading district data:{" "}
                      {districtError.message || "Something went wrong"}
                    </div>
                  ) : districtTableData.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#666",
                      }}
                    >
                      No district data available for {selectedState}
                    </div>
                  ) : (
                    <Table
                      columns={getTableColumns()}
                      dataSource={districtTableData}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        showTotal: (total) => `Total ${total} districts`,
                        size: "small",
                      }}
                      scroll={{ y: 400 }}
                      size="small"
                    />
                  )}
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IndiaMap;
